import { inject, injectable } from 'inversify';
import { makeAutoObservable, runInAction } from 'mobx';
import logger from 'js-logger';
import { notifications } from '@shared/utils/notifications';
import { queryClient } from '@shared/api/reactQuery';
import {
  getGetApiOsiServicesNeedsMonthlyConfirmationQueryOptions,
  getPostApiOsiServicesCreateAccuralsCurrentPlanMutationOptions,
  getPutApiOsiServicesIdMutationOptions
} from '@shared/api/orval/osi-services/osi-services';
import { getGetApiOsiIdV2ServicesQueryKey } from '@shared/api/orval/osi/osi';
import type { ResponsesOsiServiceResponse } from '@shared/api/orval/models/responsesOsiServiceResponse';
import type { RequestsOsiServiceRequest } from '@shared/api/orval/models/requestsOsiServiceRequest';
import type { ApiResponseInt32SystemPrivateCoreLib } from '@shared/api/orval/models/apiResponseInt32SystemPrivateCoreLib';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { type RolesService, RolesServiceIdentifier } from '@shared/types/mobx/services/roles';

export const MonthlyConfirmationServiceIdentifier = Symbol.for('MonthlyConfirmationService');

interface MonthlyFormItem {
  amount: number | null;
  uncheckFlag: boolean;
}

export interface MonthlyConfirmationService {
  isModalOpen: boolean;
  pendingServices: ResponsesOsiServiceResponse[];
  formData: Record<number, MonthlyFormItem>;
  isLoading: boolean;
  // true, если хотя бы у одной из услуг в pendingServices в текущем PlanAccural уже есть ACC-транзакции —
  // значит для этой услуги имеет смысл «доначислить» (создать ACC только для тех услуг, для которых их ещё нет).
  hasRecreate: boolean;

  loadAndOpen(): Promise<void>;

  setAmount(serviceId: number, amount: number | null): void;

  setUncheckFlag(serviceId: number, value: boolean): void;

  save(): Promise<void>;

  // Сохранить суммы (как save) и сразу же по факту сохранения — вызвать
  // POST /api/OsiServices/create-accurals-current-plan для тех же услуг.
  // Бэк идемпотентен: услуги, у которых уже есть ACC в текущем плане, пропускаются.
  saveAndRecreate(): Promise<void>;

  closeModal(): void;
}

// Доверенный (CHAIRMAN_TRUSTED) допущен к попапу доначисления наравне с председателем.
// READONLY-доверенный (CHAIRMAN_TRUSTED_READONLY) намеренно НЕ включён — доначисление это модифицирующая операция.
const ALLOWED_ROLES = new Set<string>(['CHAIRMAN', 'CHAIRMAN_TRUSTED', 'ADMIN', 'OPERATOR']);

@injectable()
export class MonthlyConfirmationServiceImpl implements MonthlyConfirmationService {
  isModalOpen = false;

  pendingServices: ResponsesOsiServiceResponse[] = [];

  formData: Record<number, MonthlyFormItem> = {};

  isLoading = false;

  constructor(
    @inject(OsiServiceIdentifier) private osiModule: OsiService,
    @inject(RolesServiceIdentifier) private rolesModule: RolesService
  ) {
    makeAutoObservable(this);
  }

  private canSee = (): boolean => {
    const currentKey = this.rolesModule.currentRole?.role;
    if (currentKey) return ALLOWED_ROLES.has(currentKey);
    // fallback: persist-store ещё не догидрировал currentRole — смотрим массив всех ролей.
    // Это закрывает race на /osi/accruals, когда useEffect срабатывает раньше hydration.
    return this.rolesModule.roles.some((r) => ALLOWED_ROLES.has(r.role));
  };

  loadAndOpen = async (): Promise<void> => {
    if (this.isLoading || this.isModalOpen) return;
    if (!this.canSee()) return;

    const { osiId } = this.osiModule;
    if (!osiId) return;

    this.isLoading = true;

    try {
      const response = await queryClient.fetchQuery(
        getGetApiOsiServicesNeedsMonthlyConfirmationQueryOptions({ osiId }, { query: { staleTime: 0, gcTime: 0 } })
      );

      const services = response?.result ?? [];

      runInAction(() => {
        this.pendingServices = services;
        this.formData = {};
        services.forEach((service) => {
          if (service.id != null) {
            this.formData[service.id] = { amount: null, uncheckFlag: false };
          }
        });

        if (services.length > 0) {
          this.isModalOpen = true;
        }
      });
    } catch (e) {
      logger.error(e);
      // молча: если бэк отвалился — модалка просто не откроется,
      // не мешаем пользователю работать со страницей начислений
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setAmount = (serviceId: number, amount: number | null): void => {
    const current = this.formData[serviceId] ?? { amount: null, uncheckFlag: false };
    this.formData[serviceId] = { ...current, amount };
  };

  setUncheckFlag = (serviceId: number, value: boolean): void => {
    const current = this.formData[serviceId] ?? { amount: null, uncheckFlag: false };
    this.formData[serviceId] = { ...current, uncheckFlag: value };
  };

  closeModal = (): void => {
    this.isModalOpen = false;
  };

  // Computed (через makeAutoObservable getter автоматически становится @computed):
  // true если у любой из услуг в текущем pendingServices у плана уже есть ACC.
  // Используется во view для условного рендера кнопки «Доначислить» в модалке.
  get hasRecreate(): boolean {
    return this.pendingServices.some((s) => s.planHasAccurals === true);
  }

  save = async (): Promise<void> => {
    const { osiId } = this.osiModule;
    if (!osiId) return;

    const tasks = this.pendingServices
      .filter((service) => service.id != null)
      .map((service) => {
        const id = service.id as number;
        const item = this.formData[id];
        if (!item) return null;
        const hasAmount = item.amount != null;
        const hasUncheck = item.uncheckFlag === true;
        if (!hasAmount && !hasUncheck) return null;

        const payload: RequestsOsiServiceRequest = {
          osiId,
          nameRu: service.nameRu ?? '',
          nameKz: service.nameKz ?? null,
          serviceGroupId: service.serviceGroupId,
          accuralMethodId: service.accuralMethodId,
          amount: hasAmount ? (item.amount as number) : service.amount,
          totalMonths: service.totalMonths ?? null,
          totalAmount: service.totalAmount ?? null,
          allowFines: service.allowFines ?? null,
          requiresMonthlyConfirmation: hasUncheck ? false : service.requiresMonthlyConfirmation ?? true
        };

        const mutation = queryClient.getMutationCache().build(queryClient, getPutApiOsiServicesIdMutationOptions());

        return mutation.execute({ id, data: payload });
      })
      .filter((task): task is Promise<unknown> => task != null);

    if (tasks.length === 0) {
      this.closeModal();
      return;
    }

    this.isLoading = true;

    try {
      const results = await Promise.allSettled(tasks);
      const hasFailure = results.some(
        (r) =>
          r.status === 'rejected' ||
          (r.status === 'fulfilled' && (r.value as { code?: number } | null | undefined)?.code !== 0)
      );

      if (hasFailure) {
        notifications.error('accruals:monthlyConfirmation.saveError');
      } else {
        notifications.success('accruals:monthlyConfirmation.savedSuccess');
        // Баг №2: принудительно обновляем список услуг V2 — тариф мог измениться;
        // без инвалидации старое значение держится в react-query кэше до перезагрузки (F5).
        await queryClient.invalidateQueries({ queryKey: getGetApiOsiIdV2ServicesQueryKey(osiId) });
        this.closeModal();
      }
    } catch (e) {
      logger.error(e);
      notifications.error('accruals:monthlyConfirmation.saveError');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // saveAndRecreate — то же что и save (PUT'ы по введённым суммам/uncheck'ам), но
  // после успеха дополнительно вызывается POST /create-accurals-current-plan по тем же osiServiceIds.
  // Бэк идемпотентен: услуги, у которых уже есть ACC в текущем плане, на бэке пропускаются.
  // Используется кнопкой «Доначислить» в модалке (показывается только если hasRecreate === true).
  saveAndRecreate = async (): Promise<void> => {
    // Guard от двойного клика / повторного запуска: не запускаем если уже идёт операция.
    if (this.isLoading) return;

    const { osiId } = this.osiModule;
    if (!osiId) return;

    // 1) Собираем id услуг по которым ЕСТЬ что сохранять (введена сумма или установлен uncheck).
    //    Эти же id отправим в POST для доначисления.
    const serviceIdsToProcess = this.pendingServices
      .filter((service) => service.id != null)
      .filter((service) => {
        const item = this.formData[service.id as number];
        return item != null && (item.amount != null || item.uncheckFlag === true);
      })
      .map((service) => service.id as number);

    if (serviceIdsToProcess.length === 0) {
      // Нечего сохранять/доначислять — мягко предупредим и закроем.
      notifications.warning('accruals:monthlyConfirmation.saveError');
      return;
    }

    // 2) Собираем PUT-задачи (та же логика, что в save()).
    const putTasks = serviceIdsToProcess
      .map((id) => {
        const service = this.pendingServices.find((s) => s.id === id);
        if (!service) return null;
        const item = this.formData[id];
        if (!item) return null;
        const hasAmount = item.amount != null;
        const hasUncheck = item.uncheckFlag === true;

        const payload: RequestsOsiServiceRequest = {
          osiId,
          nameRu: service.nameRu ?? '',
          nameKz: service.nameKz ?? null,
          serviceGroupId: service.serviceGroupId,
          accuralMethodId: service.accuralMethodId,
          amount: hasAmount ? (item.amount as number) : service.amount,
          totalMonths: service.totalMonths ?? null,
          totalAmount: service.totalAmount ?? null,
          allowFines: service.allowFines ?? null,
          requiresMonthlyConfirmation: hasUncheck ? false : service.requiresMonthlyConfirmation ?? true
        };

        const mutation = queryClient.getMutationCache().build(queryClient, getPutApiOsiServicesIdMutationOptions());
        return mutation.execute({ id, data: payload });
      })
      .filter((task): task is Promise<unknown> => task != null);

    this.isLoading = true;

    try {
      // 3) Сначала PUT'ы — как в save(). Если хотя бы один упал — не идём в POST.
      const results = await Promise.allSettled(putTasks);
      const hasFailure = results.some(
        (r) =>
          r.status === 'rejected' ||
          (r.status === 'fulfilled' && (r.value as { code?: number } | null | undefined)?.code !== 0)
      );

      if (hasFailure) {
        notifications.error('accruals:monthlyConfirmation.saveError');
        return;
      }

      // 4) POST /create-accurals-current-plan — доначисление по выбранным услугам.
      //    Шлём ВСЕ id, по которым было что сохранять (бэк сам пропустит уже начисленные через guard).
      const recreateMutation = queryClient
        .getMutationCache()
        .build(queryClient, getPostApiOsiServicesCreateAccuralsCurrentPlanMutationOptions());

      const recreateResponse = (await recreateMutation.execute({
        data: { osiId, osiServiceIds: serviceIdsToProcess }
      })) as ApiResponseInt32SystemPrivateCoreLib | null | undefined;

      if (recreateResponse && recreateResponse.code !== 0) {
        notifications.error(recreateResponse.message ?? 'accruals:monthlyConfirmation.saveError');
        return;
      }

      // 5) Успех: суммы сохранены и услуги доначислены (или были уже начислены — бэк вернёт result=0).
      const created = recreateResponse?.result ?? 0;
      if (created > 0) {
        notifications.success('accruals:monthlyConfirmation.recreateSuccess');
      } else {
        notifications.info('accruals:monthlyConfirmation.recreateInfo');
      }
      // Баг №2: принудительно обновляем список услуг V2 — тариф мог измениться;
      // без инвалидации старое значение держится в react-query кэше до перезагрузки (F5).
      await queryClient.invalidateQueries({ queryKey: getGetApiOsiIdV2ServicesQueryKey(osiId) });
      this.closeModal();
    } catch (e) {
      logger.error(e);
      notifications.error('accruals:monthlyConfirmation.saveError');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };
}
